import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion } from 'framer-motion';
import { FaBookOpen, FaChevronLeft, FaChevronRight, FaList, FaClipboardList } from 'react-icons/fa';
import { CheckCircle, RotateCw, Clock } from 'lucide-react';
import ShareButton from '../common/ShareButton';

const easing = [0.22, 1, 0.36, 1];

// Clean progress line for cards — same treatment as the hero
const MiniTrail = ({ progress, tone = 'primary', className = 'w-24' }) => {
  const clamped = Math.max(0, Math.min(100, progress || 0));
  const fillClass = tone === 'mission' ? 'bg-mission' : 'bg-primary';
  return (
    <div className={`relative h-[2px] bg-foreground/10 ${className}`} aria-hidden="true">
      <div
        className={`absolute inset-y-0 left-0 ${fillClass} transition-all duration-700`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};


const CourseCarouselSection = ({
  pathCourses = [],
  userCurrentCourse,
  userProgress,
  isCourseCompleted,
  getCourseProgress,
  getCourseContent,
  onCourseClick,
  navigationContext = null,
  modalNavigation = null,
  onStartCourse,
  progressLoading = false,
  coursesLoading = false,
  loadingCourseId = null,
  getStartButtonText,
  needsProfileCompletion = false
}) => {
  const [carouselStartIndex, setCarouselStartIndex] = useState(0);
  const [showAllCoursesModal, setShowAllCoursesModal] = useState(false);
  const [mobileScrollPosition, setMobileScrollPosition] = useState(0);
  const [mobileNavState, setMobileNavState] = useState({ canGoPrevious: false, canGoNext: false });
  const mobileScrollRef = React.useRef(null);

  // Smart carousel logic for positioning courses
  const getSmartCarouselPosition = () => {
    const allCourses = pathCourses;
    const totalCourses = allCourses.length;

    if (totalCourses <= 3) {
      // Show all courses, no navigation needed
      return {
        startIndex: 0,
        canGoPrevious: false,
        canGoNext: false
      };
    }

    // Find the current course index in all courses
    let currentCourseIndex = -1;
    if (userCurrentCourse) {
      currentCourseIndex = allCourses.findIndex(course => course._id === userCurrentCourse._id);
    }

    let idealStartIndex = 0;

    if (currentCourseIndex !== -1) {
      // Try to center the current course in the 3-course view
      // Ideally: [previous, current, next]
      idealStartIndex = Math.max(0, currentCourseIndex - 1);

      // Ensure we don't go beyond the available courses
      idealStartIndex = Math.min(idealStartIndex, totalCourses - 3);
    }

    // Respect user's manual navigation (carousel buttons)
    const actualStartIndex = Math.max(0, Math.min(carouselStartIndex, totalCourses - 3));

    return {
      startIndex: actualStartIndex,
      canGoPrevious: actualStartIndex > 0,
      canGoNext: actualStartIndex + 3 < totalCourses
    };
  };

  // Get courses for current carousel view (3 courses at a time with smart positioning)
  const getVisibleCourses = () => {
    const allCourses = pathCourses;
    const position = getSmartCarouselPosition();
    return allCourses.slice(position.startIndex, position.startIndex + 3);
  };

  // Get navigation states from smart positioning (for desktop)
  const { canGoPrevious: canGoPreviousDesktop, canGoNext: canGoNextDesktop } = getSmartCarouselPosition();

  // Mobile navigation logic based on scroll position
  const getMobileNavigationState = () => {
    if (!mobileScrollRef.current) return { canGoPrevious: false, canGoNext: false };
    
    const scrollLeft = mobileScrollRef.current.scrollLeft;
    const scrollWidth = mobileScrollRef.current.scrollWidth;
    const clientWidth = mobileScrollRef.current.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;
    
    return {
      canGoPrevious: scrollLeft > 5, // Small tolerance for start
      canGoNext: scrollLeft < (maxScrollLeft - 5) // Small tolerance for end
    };
  };

  // Desktop navigation functions
  const goToPrevious = () => {
    if (!canGoPreviousDesktop) return;
    setCarouselStartIndex(prev => Math.max(0, prev - 1));
  };

  const goToNext = () => {
    if (!canGoNextDesktop) return;
    const allCourses = pathCourses;
    setCarouselStartIndex(prev => Math.min(prev + 1, allCourses.length - 3));
  };

  // Mobile navigation functions
  const goToPreviousMobile = () => {
    if (!mobileNavState.canGoPrevious || !mobileScrollRef.current) return;

    const cardWidth = 176; // w-44 = 176px
    const gap = 12; // space-x-3 = 12px
    const scrollAmount = (cardWidth + gap) * 2; // Scroll by ~2 cards
    const currentScroll = mobileScrollRef.current.scrollLeft;

    // If we're close to the beginning, scroll to the very beginning
    if (currentScroll - scrollAmount <= 20) {
      mobileScrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      mobileScrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const goToNextMobile = () => {
    if (!mobileNavState.canGoNext || !mobileScrollRef.current) return;

    const cardWidth = 176; // w-44 = 176px
    const gap = 12; // space-x-3 = 12px
    const scrollAmount = (cardWidth + gap) * 2; // Scroll by ~2 cards
    const currentScroll = mobileScrollRef.current.scrollLeft;
    const maxScroll = mobileScrollRef.current.scrollWidth - mobileScrollRef.current.clientWidth;
    
    // If we're close to the end, scroll to the very end
    if (currentScroll + scrollAmount >= maxScroll - 20) {
      mobileScrollRef.current.scrollTo({ left: maxScroll, behavior: 'smooth' });
    } else {
      mobileScrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Handle mobile scroll events to update button states
  const handleMobileScroll = () => {
    if (mobileScrollRef.current) {
      setMobileScrollPosition(mobileScrollRef.current.scrollLeft);
      updateMobileNavState();
    }
  };

  // Update mobile navigation state
  const updateMobileNavState = () => {
    if (mobileScrollRef.current && pathCourses.length > 0) {
      const scrollLeft = mobileScrollRef.current.scrollLeft;
      const scrollWidth = mobileScrollRef.current.scrollWidth;
      const clientWidth = mobileScrollRef.current.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;
      
      setMobileNavState({
        canGoPrevious: scrollLeft > 5,
        canGoNext: scrollLeft < (maxScrollLeft - 5)
      });
    }
  };

  // Smart carousel reset based on completion status
  useEffect(() => {
    const allCourses = pathCourses;
    if (allCourses.length <= 3) {
      setCarouselStartIndex(0);
    } else if (userProgress?.isPathCompleted || (userProgress?.pathProgress?.percentageComplete || 0) >= 100) {
      // Show last 3 courses when path is completed
      setCarouselStartIndex(Math.max(0, allCourses.length - 3));
    } else if (userCurrentCourse) {
      // Try to center current course
      const currentIndex = allCourses.findIndex(course => course._id === userCurrentCourse._id);
      if (currentIndex !== -1) {
        const centeredStart = Math.max(0, Math.min(currentIndex - 1, allCourses.length - 3));
        setCarouselStartIndex(centeredStart);
      }
    } else {
      setCarouselStartIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCurrentCourse?._id, userProgress?.isPathCompleted, userProgress?.pathProgress?.percentageComplete, pathCourses.length]);

  // Initialize mobile navigation state after mount and when content changes
  useEffect(() => {
    const initializeMobileNavState = () => {
      if (mobileScrollRef.current && pathCourses.length > 0) {
        // Small delay to ensure scroll dimensions are calculated
        setTimeout(() => {
          updateMobileNavState();
        }, 100);
      } else {
        // Default state when no content or ref not ready
        setMobileNavState({ canGoPrevious: false, canGoNext: pathCourses.length > 3 });
      }
    };

    initializeMobileNavState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathCourses.length]);

  // Manage body scroll when modal is open
  useEffect(() => {
    if (showAllCoursesModal) {
      // Prevent body scroll when modal is open
      document.body.classList.add('modal-open');
    } else {
      // Restore body scroll when modal is closed
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showAllCoursesModal]);

  const handleCourseClick = (course) => {
    if (onCourseClick) {
      onCourseClick(course);
    }
  };

  return (
    <>
      {/* Courses Section */}
      <div className="bg-background px-5 sm:px-10 py-8 sm:py-12">
        {/* Section header — editorial */}
        <div className="flex items-end justify-between mb-7 sm:mb-9 gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-2">
              <span className="inline-block h-px w-5 bg-primary" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
                Path
              </span>
            </div>
            <h2
              className="font-display font-bold text-foreground tracking-tight leading-none"
              style={{ fontSize: 'clamp(20px, 2.6vw, 28px)', letterSpacing: '-0.02em' }}
            >
              Your Courses
            </h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setShowAllCoursesModal(true)}
              className="sm:hidden text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/55 hover:text-primary transition-colors flex items-center gap-1.5"
              title="View all courses"
            >
              <FaList className="w-3 h-3" />
              All
            </button>
            {pathCourses.length > 3 && (
              <div className="hidden sm:flex items-center gap-1">
                <button
                  onClick={goToPrevious}
                  disabled={!canGoPreviousDesktop}
                  className={`w-8 h-8 flex items-center justify-center transition-colors ${
                    canGoPreviousDesktop
                      ? 'text-foreground/70 hover:text-primary cursor-pointer'
                      : 'text-foreground/20 cursor-not-allowed'
                  }`}
                  aria-label="Previous"
                >
                  <FaChevronLeft className="w-3 h-3" />
                </button>
                <button
                  onClick={goToNext}
                  disabled={!canGoNextDesktop}
                  className={`w-8 h-8 flex items-center justify-center transition-colors ${
                    canGoNextDesktop
                      ? 'text-foreground/70 hover:text-primary cursor-pointer'
                      : 'text-foreground/20 cursor-not-allowed'
                  }`}
                  aria-label="Next"
                >
                  <FaChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Horizontal Scroll Layout */}
        <div className="block sm:hidden">
          <div className="relative">
            {/* Overlaid arrow buttons */}
            {mobileNavState.canGoPrevious && (
              <button
                onClick={goToPreviousMobile}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 border border-border shadow flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <FaChevronLeft className="w-3 h-3" />
              </button>
            )}
            {mobileNavState.canGoNext && (
              <button
                onClick={goToNextMobile}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white/90 border border-border shadow flex items-center justify-center text-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <FaChevronRight className="w-3 h-3" />
              </button>
            )}
          <div
            ref={mobileScrollRef}
            className="overflow-x-auto scrollbar-hide scroll-smooth"
            onScroll={handleMobileScroll}
          >
            <div className="flex gap-4 pb-4 px-1">
              {pathCourses.map((course) => {
                const courseIndex = pathCourses.findIndex(c => c._id === course._id);
                const isCompleted = isCourseCompleted(course._id);
                const isCurrent = !userProgress?.isPathCompleted && userCurrentCourse?._id === course._id;
                const shouldShowAsCompleted = userProgress?.isPathCompleted || isCompleted;
                const progress = getCourseProgress(course._id);
                const isLoadingThis = loadingCourseId === course._id;
                const hasThumb = course.thumbnail && (course.thumbnail.url || course.thumbnail.data);

                const status = shouldShowAsCompleted
                  ? { text: 'Completed', tone: 'text-primary', dot: 'bg-primary' }
                  : isCurrent
                    ? { text: 'In Progress', tone: 'text-primary', dot: 'bg-primary' }
                    : { text: 'Not Started', tone: 'text-foreground/40', dot: 'bg-foreground/25' };

                return (
                  <article
                    key={course._id}
                    className={`flex-shrink-0 w-[calc(70%-6px)] relative cursor-pointer flex flex-col bg-background ${isLoadingThis ? 'pointer-events-none' : ''}`}
                    onClick={() => handleCourseClick(course)}
                  >
                    {isLoadingThis && (
                      <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                        <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    <div
                      className="relative w-full aspect-[4/3] bg-secondary overflow-hidden"
                      style={{ borderRadius: '4px' }}
                    >
                      {hasThumb ? (
                        <img
                          src={course.thumbnail.url || `data:${course.thumbnail.contentType};base64,${course.thumbnail.data}`}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <FaBookOpen className="w-6 h-6 text-primary/35" />
                        </div>
                      )}
                      <div
                        className="absolute top-2 left-2 px-1.5 py-0.5 bg-background/90 backdrop-blur-sm"
                        style={{ borderRadius: '2px' }}
                      >
                        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-foreground/70 tabular-nums">
                          {String(courseIndex + 1).padStart(2, '0')}
                        </span>
                      </div>
                    </div>

                    <div className="pt-3.5">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className={`w-1 h-1 rounded-full ${status.dot}`} aria-hidden="true" />
                        <span className={`text-[9px] font-bold uppercase tracking-[0.18em] ${status.tone}`}>
                          {status.text}
                          {!shouldShowAsCompleted && progress > 0 && (
                            <span className="ml-1 text-foreground/40 tabular-nums">· {progress}%</span>
                          )}
                        </span>
                      </div>
                      <h3
                        className="font-display font-bold text-foreground leading-tight line-clamp-2 mb-3"
                        style={{ fontSize: '15px', letterSpacing: '-0.01em' }}
                      >
                        {course.title}
                      </h3>
                      <MiniTrail
                        progress={progress}
                        tone={shouldShowAsCompleted ? 'success' : 'primary'}
                        className="w-full"
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
          {coursesLoading && pathCourses.length === 0 && (
            <div className="flex gap-3 pb-4">
              {[0, 1, 2].map(i => (
                <div key={i} className="flex-shrink-0 w-[calc(50%-6px)] rounded-xl bg-muted animate-pulse">
                  <div className="h-28 bg-muted rounded-t-xl" />
                  <div className="p-2.5 space-y-2">
                    <div className="h-3 bg-muted-foreground/10 rounded w-3/4" />
                    <div className="h-1 bg-muted-foreground/10 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Desktop Grid Layout — editorial chapter cards */}
        <div className="hidden sm:block">
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-8"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } } }}
          >
            {getVisibleCourses().map((course) => {
              const courseIndex = pathCourses.findIndex(c => c._id === course._id);
              const isCompleted = isCourseCompleted(course._id);
              const isCurrent = !userProgress?.isPathCompleted && userCurrentCourse?._id === course._id;
              const isBeingViewed = modalNavigation?.isViewingBackwards && modalNavigation?.viewingCourse?._id === course._id;
              const shouldShowAsCompleted = userProgress?.isPathCompleted || isCompleted;
              const progress = getCourseProgress(course._id);
              const courseContent = getCourseContent(course);
              const lessonsCount = courseContent.filter(item => item.type === 'lesson').length;
              const submissionsCount = courseContent.filter(item => item.type === 'submissionForm').length;

              const status = isBeingViewed
                ? { text: 'Reviewing', tone: 'text-foreground/55', dot: 'bg-foreground/40' }
                : shouldShowAsCompleted
                  ? { text: 'Completed', tone: 'text-primary', dot: 'bg-primary' }
                  : isCurrent
                    ? { text: 'In Progress', tone: 'text-primary', dot: 'bg-primary' }
                    : { text: 'Not Started', tone: 'text-foreground/40', dot: 'bg-foreground/25' };

              const ctaText = shouldShowAsCompleted ? 'View' : (isCurrent && needsProfileCompletion) ? 'Complete Profile to Start' : isCurrent ? 'Jump Back In' : 'View Course';
              const isLoadingThis = loadingCourseId === course._id;
              const hasThumb = course.thumbnail && (course.thumbnail.url || course.thumbnail.data);

              return (
                <motion.article
                  key={course._id}
                  className={`group relative cursor-pointer flex flex-col bg-background ${isLoadingThis ? 'pointer-events-none' : ''}`}
                  onClick={() => handleCourseClick(course)}
                  variants={{
                    hidden: { opacity: 0, y: 16 },
                    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: easing } }
                  }}
                  whileHover={{ y: -3, transition: { duration: 0.2, ease: easing } }}
                >
                  {/* Loading overlay */}
                  {isLoadingThis && (
                    <div className="absolute inset-0 bg-background/80 flex items-center justify-center z-10">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}

                  {/* Thumbnail — branded, with a soft 1px hairline; status sits below as a stamp */}
                  <div
                    className="relative w-full aspect-[4/3] bg-secondary overflow-hidden"
                    style={{ borderRadius: '4px' }}
                  >
                    {hasThumb ? (
                      <img
                        src={course.thumbnail.url || `data:${course.thumbnail.contentType};base64,${course.thumbnail.data}`}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FaBookOpen className="w-8 h-8 text-primary/35" />
                      </div>
                    )}
                    {/* Course index stamp in the corner */}
                    <div
                      className="absolute top-3 left-3 px-2 py-1 bg-background/90 backdrop-blur-sm"
                      style={{ borderRadius: '2px' }}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-foreground/70 tabular-nums">
                        Course {String(courseIndex + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="pt-5 flex flex-col flex-1">
                    {/* Status eyebrow */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} aria-hidden="true" />
                      <span className={`text-[10px] font-bold uppercase tracking-[0.22em] ${status.tone}`}>
                        {status.text}
                      </span>
                      {!shouldShowAsCompleted && progress > 0 && (
                        <span className="text-[10px] font-bold tabular-nums text-foreground/40">
                          · {progress}%
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3
                      className="font-display font-bold text-foreground leading-tight mb-2 line-clamp-2 group-hover:text-primary transition-colors"
                      style={{ fontSize: '20px', letterSpacing: '-0.015em' }}
                    >
                      {course.title}
                    </h3>

                    {/* Description */}
                    <p className="text-[14px] text-foreground/65 leading-snug line-clamp-2 mb-4 flex-1">
                      {course.description || 'No description available'}
                    </p>

                    {/* Progress line + counts */}
                    <div className="mb-4">
                      <MiniTrail
                        progress={progress}
                        tone={shouldShowAsCompleted ? 'success' : 'primary'}
                        className="w-full"
                      />
                      <div className="mt-2 flex items-baseline justify-between text-[11px] font-bold uppercase tracking-[0.14em] tabular-nums">
                        <span className={shouldShowAsCompleted ? 'text-primary' : 'text-foreground/55'}>
                          {progress}<span className="opacity-60">%</span>
                        </span>
                        <div className="flex items-center gap-3 text-foreground/55">
                          {lessonsCount > 0 && (
                            <span className="flex items-center gap-1">
                              <FaBookOpen className="w-2.5 h-2.5" />
                              {lessonsCount}
                            </span>
                          )}
                          {submissionsCount > 0 && (
                            <span className="flex items-center gap-1">
                              <FaClipboardList className="w-2.5 h-2.5" />
                              {submissionsCount}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* CTA — text link */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isCurrent && !shouldShowAsCompleted && onStartCourse) {
                          onStartCourse();
                        } else {
                          handleCourseClick(course);
                        }
                      }}
                      disabled={progressLoading && isCurrent}
                      className="inline-flex items-center gap-1.5 self-start text-[12px] font-bold uppercase tracking-[0.18em] text-foreground hover:text-primary transition-colors"
                    >
                      <span>{ctaText}</span>
                      <svg className="w-3 h-3 transition-transform group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </motion.article>
              );
            })}
            {coursesLoading && pathCourses.length === 0 && (
              <>
                {[0, 1, 2].map(i => (
                  <div key={i} className="flex flex-col animate-pulse">
                    <div className="aspect-[4/3] bg-muted" style={{ borderRadius: '4px' }} />
                    <div className="pt-5 space-y-3">
                      <div className="h-2 bg-muted rounded-full w-1/4" />
                      <div className="h-5 bg-muted rounded-full w-3/4" />
                      <div className="h-3 bg-muted rounded-full w-full" />
                      <div className="h-3 bg-muted rounded-full w-2/3" />
                    </div>
                  </div>
                ))}
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* Modal for All Courses */}
      {showAllCoursesModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl border border-gray-200 flex flex-col max-h-[90vh]">
            {/* Fixed Header */}
            <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-start justify-between gap-2 min-w-0">
                <h3 className="text-base sm:text-2xl font-bold text-[#054581] flex-1 min-w-0 break-words pr-2">All Path Courses</h3>

                <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0 whitespace-nowrap">
                  {/* Continue/Start Course Button */}
                  {!userProgress?.isPathCompleted && onStartCourse && getStartButtonText && (
                    <div className="scale-[0.65] sm:scale-100 origin-right">
                      <ShareButton
                        onClick={() => {
                          setShowAllCoursesModal(false);
                          onStartCourse();
                        }}
                        disabled={progressLoading}
                        loading={progressLoading}
                        text={getStartButtonText()}
                      />
                    </div>
                  )}

                  <button
                    onClick={() => setShowAllCoursesModal(false)}
                    className="text-gray-400 hover:text-[#1086a4] text-2xl sm:text-3xl font-light transition-colors flex-shrink-0 ml-1"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>

            {/* Scrollable Content with Custom Scrollbar */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6 custom-scrollbar">
              {pathCourses.map((course, index) => {
                // Determine course status
                const isCompleted = isCourseCompleted(course._id);
                const isCurrent = userCurrentCourse?._id === course._id;

                // When path is completed, all courses should show as completed
                const shouldShowAsCompleted = userProgress?.isPathCompleted || isCompleted;

                // Define styling based on status
                let borderClass, bgClass, shadowClass;
                if (shouldShowAsCompleted) {
                  borderClass = 'border-green-200';
                  bgClass = 'bg-green-50';
                  shadowClass = 'shadow-sm';
                } else if (isCurrent) {
                  borderClass = 'border-[#1086a4] border-2';
                  bgClass = 'bg-blue-50';
                  shadowClass = 'shadow-md';
                } else {
                  borderClass = 'border-gray-200';
                  bgClass = 'bg-gray-50';
                  shadowClass = '';
                }

                return (
                  <div
                    key={course._id}
                    className={`rounded-xl border transition-all duration-200 overflow-hidden ${borderClass} ${bgClass} ${shadowClass} cursor-pointer`}
                    onClick={() => {
                      handleCourseClick(course);
                      setShowAllCoursesModal(false);
                    }}
                  >
                    {/* Mobile Layout: Vertical Stack */}
                    <div className="block sm:hidden">
                      {/* Course Thumbnail - Full Width on Mobile */}
                      <div className="w-full h-48 relative bg-gray-100">
                        {course.thumbnail && (course.thumbnail.url || course.thumbnail.data) ? (
                          <img
                            src={course.thumbnail.url || `data:${course.thumbnail.contentType};base64,${course.thumbnail.data}`}
                            alt={course.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('bg-[#F8F2FF]');
                              e.target.parentElement.innerHTML = `
                                <div class="absolute inset-0 flex items-center justify-center">
                                  <div class="w-12 h-12 bg-[#1086a4] bg-opacity-10 rounded-full flex items-center justify-center">
                                    <svg class="w-6 h-6 text-[#1086a4]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                                    </svg>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        ) : (
                          <div className="absolute inset-0 bg-[#F8F2FF] flex items-center justify-center">
                            <div className="w-12 h-12 bg-[#1086a4] bg-opacity-10 rounded-full flex items-center justify-center">
                              <FaBookOpen className="w-6 h-6 text-[#1086a4]" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Course Content - Stacked on Mobile */}
                      <div className="p-4">
                        {/* Header with Course Number and Status */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold text-[#054581] bg-[#054581] bg-opacity-10 w-6 h-6 rounded flex items-center justify-center">
                              {index + 1}
                            </span>
                            {(() => {
                              if (shouldShowAsCompleted) {
                                return (
                                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                    <CheckCircle size={12} />
                                    Completed
                                  </span>
                                );
                              } else if (isCurrent) {
                                return (
                                  <span className="bg-[#1086a4] text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                    <RotateCw size={12} />
                                    Current
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                    <Clock size={12} />
                                    Pending
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </div>

                        {/* Course Title */}
                        <h4 className="text-base font-bold text-[#054581] mb-2">{course.title}</h4>

                        {/* Course Description */}
                        <p className="text-[#1e2d40] text-sm mb-3 line-clamp-2">
                          {course.description || 'No description available'}
                        </p>

                        {/* Course Stats */}
                        <div className="space-y-1">
                          <div className="flex items-center text-xs text-gray-500">
                            <FaBookOpen className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{(() => {
                              const courseContent = getCourseContent(course);
                              const lessonsCount = courseContent.filter(item => item.type === 'lesson').length;
                              const submissionFormsCount = courseContent.filter(item => item.type === 'submissionForm').length;
                              
                              if (lessonsCount > 0 && submissionFormsCount > 0) {
                                return `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} + ${submissionFormsCount} mission${submissionFormsCount !== 1 ? 's' : ''}`;
                              } else if (lessonsCount > 0) {
                                return `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''}`;
                              } else if (submissionFormsCount > 0) {
                                return `${submissionFormsCount} mission${submissionFormsCount !== 1 ? 's' : ''}`;
                              } else {
                                return '0 lessons';
                              }
                            })()}</span>
                          </div>
                          <div className="text-xs font-medium text-[#054581]">
                            {getCourseProgress(course._id)}% Complete
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Desktop Layout: Horizontal Card */}
                    <div className="hidden sm:flex">
                      {/* Thumbnail Section */}
                      <div className="w-72 h-44 relative flex-shrink-0">
                        {course.thumbnail && (course.thumbnail.url || course.thumbnail.data) ? (
                          <img
                            src={course.thumbnail.url || `data:${course.thumbnail.contentType};base64,${course.thumbnail.data}`}
                            alt={course.title}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('bg-[#F8F2FF]');
                              e.target.parentElement.innerHTML = `
                                <div class="absolute inset-0 flex items-center justify-center">
                                  <div class="w-16 h-16 bg-[#1086a4] bg-opacity-10 rounded-full flex items-center justify-center">
                                    <svg class="w-8 h-8 text-[#1086a4]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                                    </svg>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        ) : (
                          <div className="absolute inset-0 bg-[#F8F2FF] flex items-center justify-center">
                            <div className="w-16 h-16 bg-[#1086a4] bg-opacity-10 rounded-full flex items-center justify-center">
                              <FaBookOpen className="w-8 h-8 text-[#1086a4]" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Content Section */}
                      <div className="flex-1 p-6">
                        {/* Header with Course Number and Status */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <span className="text-lg font-bold text-[#054581] bg-[#054581] bg-opacity-10 w-8 h-8 rounded-lg flex items-center justify-center">
                              {index + 1}
                            </span>
                            {(() => {
                              if (shouldShowAsCompleted) {
                                return (
                                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                                    <CheckCircle size={14} />
                                    Completed
                                  </span>
                                );
                              } else if (isCurrent) {
                                return (
                                  <span className="bg-[#1086a4] text-white px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                                    <RotateCw size={14} />
                                    Current
                                  </span>
                                );
                              } else {
                                return (
                                  <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                                    <Clock size={14} />
                                    Pending
                                  </span>
                                );
                              }
                            })()}
                          </div>
                        </div>

                        {/* Course Title */}
                        <h4 className="text-xl font-bold text-[#054581] mb-3">{course.title}</h4>

                        {/* Course Description */}
                        <p className="text-[#1e2d40] text-sm mb-4 line-clamp-2">
                          {course.description || 'No description available'}
                        </p>

                        {/* Course Stats */}
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <div className="flex items-center">
                            <FaBookOpen className="w-4 h-4 mr-2" />
                            <span>{(() => {
                              const courseContent = getCourseContent(course);
                              const lessonsCount = courseContent.filter(item => item.type === 'lesson').length;
                              const submissionFormsCount = courseContent.filter(item => item.type === 'submissionForm').length;
                              
                              if (lessonsCount > 0 && submissionFormsCount > 0) {
                                return `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''} + ${submissionFormsCount} mission${submissionFormsCount !== 1 ? 's' : ''}`;
                              } else if (lessonsCount > 0) {
                                return `${lessonsCount} lesson${lessonsCount !== 1 ? 's' : ''}`;
                              } else if (submissionFormsCount > 0) {
                                return `${submissionFormsCount} mission${submissionFormsCount !== 1 ? 's' : ''}`;
                              } else {
                                return '0 lessons';
                              }
                            })()}</span>
                          </div>
                          <div className="text-right">
                            <div className="font-medium text-[#054581]">
                              {getCourseProgress(course._id)}% Complete
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CourseCarouselSection; 